<script setup lang="ts">
import { ref, computed } from "vue";
import { useTranslation } from "i18next-vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import type { Catalogue } from "@/types/catalogues.types";

const props = defineProps<{
  modelValue: boolean;
  catalogues: Catalogue[];
  findingsCount: number;
  existingAssociationsCount: number;
  loading?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (
    e: "confirm",
    payload: { catalogueIds: number[]; replaceExisting: boolean },
  ): void;
}>();

const { t } = useTranslation();

// Local state
const selectedCatalogueIds = ref<number[]>([]);
const replaceExisting = ref(false);

// Computed
const findingsToProcess = computed(() => {
  if (replaceExisting.value) {
    return props.findingsCount;
  }
  return props.findingsCount - props.existingAssociationsCount;
});

const canConfirm = computed(() => {
  return selectedCatalogueIds.value.length > 0 && findingsToProcess.value > 0;
});

// Methods
const toggleCatalogue = (catalogueId: number) => {
  const index = selectedCatalogueIds.value.indexOf(catalogueId);
  if (index === -1) {
    selectedCatalogueIds.value.push(catalogueId);
  } else {
    selectedCatalogueIds.value.splice(index, 1);
  }
};

const isCatalogueSelected = (catalogueId: number) => {
  return selectedCatalogueIds.value.includes(catalogueId);
};

const handleConfirm = () => {
  emit("confirm", {
    catalogueIds: selectedCatalogueIds.value,
    replaceExisting: replaceExisting.value,
  });
};

const handleClose = () => {
  emit("update:modelValue", false);
};

// Reset on open
const handleOpen = () => {
  selectedCatalogueIds.value = [];
  replaceExisting.value = false;
};
</script>

<template>
  <el-dialog
    class="iqoa-catalogue-modal"
    :model-value="modelValue"
    :show-close="false"
    fullscreen
    @update:model-value="$emit('update:modelValue', $event)"
    @open="handleOpen"
  >
    <template #header>
      <div class="modal-header">
        <div class="header-content">
          <div class="header-icon">
            <i class="bi-book"></i>
          </div>
          <div class="header-text">
            <h2 class="header-title">{{ t("report:ai.iqoa.modal.title") }}</h2>
            <p class="header-description">
              {{ t("report:ai.iqoa.modal.description") }}
            </p>
          </div>
        </div>
        <button class="close-btn" @click="handleClose">
          <i class="bi-x-lg"></i>
        </button>
      </div>
    </template>

    <div class="iqoa-modal-content">
      <!-- Catalogues section title -->
      <h4 class="section-title">
        {{ t("report:ai.iqoa.modal.selectCatalogues") }}
      </h4>

      <!-- Catalogues grid (scrollable) -->
      <div class="catalogues-scroll-area">
        <div v-if="catalogues.length === 0" class="empty-state">
          <i class="bi-book text-muted" />
          <span>{{ t("report:ai.iqoa.modal.noCatalogues") }}</span>
        </div>

        <div v-else class="catalogues-grid">
          <div
            v-for="catalogue in catalogues"
            :key="catalogue.id"
            class="catalogue-card"
            :class="{ selected: isCatalogueSelected(catalogue.id) }"
            @click="toggleCatalogue(catalogue.id)"
          >
            <div class="card-checkbox">
              <el-checkbox
                :model-value="isCatalogueSelected(catalogue.id)"
                @click.stop
                @change="toggleCatalogue(catalogue.id)"
              />
            </div>
            <div class="card-icon">
              <i class="bi-journal-bookmark"></i>
            </div>
            <div class="card-info">
              <span class="catalogue-name">{{ catalogue.name }}</span>
              <span v-if="catalogue.type" class="catalogue-type">
                {{ catalogue.type }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="modal-footer">
        <!-- Options section -->
        <div class="footer-options">
          <!-- Replace existing option -->
          <div v-if="existingAssociationsCount > 0" class="option-section">
            <el-checkbox v-model="replaceExisting">
              <span class="option-label">
                {{ t("report:ai.iqoa.modal.replaceExisting") }}
              </span>
            </el-checkbox>
            <p class="option-hint">
              {{
                t("report:ai.iqoa.modal.existingCount", {
                  count: existingAssociationsCount,
                })
              }}
            </p>
          </div>

          <!-- Summary -->
          <div class="summary-section">
            <i class="bi-info-circle" />
            <span>
              {{
                t("report:ai.iqoa.modal.findingsToProcess", {
                  count: findingsToProcess,
                })
              }}
            </span>
          </div>
        </div>

        <!-- Action buttons -->
        <div class="footer-actions">
          <DRBtn
            variant="outline"
            size="lg"
            :label="t('common:cancel')"
            @click="handleClose"
          />
          <DRBtn
            variant="primary"
            size="lg"
            :label="t('report:ai.iqoa.modal.start')"
            :disabled="!canConfirm"
            :loading="loading"
            @click="handleConfirm"
          />
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
/* Modal header */
.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 32px;
  border-bottom: 1px solid var(--el-border-color);
  background-color: var(--el-bg-color);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--bs-ia) 0%, #6366f1 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  flex-shrink: 0;
}

.header-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.header-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: var(--el-text-color-primary);
}

.header-description {
  font-size: 14px;
  color: var(--el-text-color-secondary);
  margin: 0;
}

.close-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--el-text-color-secondary);
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover {
    background-color: var(--el-fill-color);
    color: var(--el-text-color-primary);
  }
}

/* Modal content */
.iqoa-modal-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 24px 32px;
  overflow: hidden;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 16px 0;
  color: var(--el-text-color-primary);
  flex-shrink: 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px;
  color: var(--el-text-color-secondary);

  i {
    font-size: 48px;
  }
}

/* Scrollable catalogues area */
.catalogues-scroll-area {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0 -32px;
  padding: 0 32px 16px 32px;
}

/* Responsive grid */
.catalogues-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

/* Catalogue card */
.catalogue-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  border: 2px solid var(--el-border-color);
  background-color: var(--el-bg-color);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--el-color-primary-light-5);
    background-color: var(--el-fill-color-light);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  &.selected {
    border-color: var(--el-color-primary);
    background-color: var(--el-color-primary-light-9);

    .card-icon {
      background-color: var(--el-color-primary);
      color: white;
    }
  }
}

.card-checkbox {
  flex-shrink: 0;
}

.card-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background-color: var(--el-fill-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: var(--el-text-color-secondary);
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.card-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.catalogue-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--el-text-color-primary);
  word-break: break-word;
}

.catalogue-type {
  font-size: 13px;
  color: var(--el-text-color-secondary);
}

/* Modal footer */
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 20px 32px;
  border-top: 1px solid var(--el-border-color);
  background-color: var(--el-bg-color);
}

.footer-options {
  display: flex;
  align-items: center;
  gap: 24px;
  flex: 1;
}

.option-section {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.option-label {
  font-size: 14px;
}

.option-hint {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  margin: 0;
  padding-left: 24px;
}

.summary-section {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background-color: var(--el-color-primary-light-9);
  border-radius: 8px;
  color: var(--el-color-primary);
  font-size: 14px;
  font-weight: 500;

  i {
    font-size: 16px;
  }
}

.footer-actions {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}
</style>

<style lang="scss">
/* Global styles for fullscreen dialog */
.iqoa-catalogue-modal.el-dialog {
  display: flex;
  flex-direction: column;

  .el-dialog__header {
    padding: 0;
    margin: 0;
  }

  .el-dialog__body {
    flex: 1;
    padding: 0;
    overflow: hidden;
  }

  .el-dialog__footer {
    padding: 0;
  }
}
</style>
